// import TablePagination from "@/components/table/pagination";
import PermissionsLists from "@/config/PermissionsLists";
import { EndPoints, httpService, Operation } from "@/Core/Client";
import { useState } from "react";

// import TablePagination from "@/components/table/pagination";
import { Config } from "@/components/crud/crud-config";
import CrudFormElements from "@/components/crud/crud-form-elements";
import CrudPage from "@/components/crud/page";
import { DynamicBadge } from "@/components/dynamic-badge";
import PencilIcon from "@/components/icons/pencil";
import TableActions from "@/components/table-actions";
import { getUserStatusColors } from "@/components/table-utils/get-status-color";
import useQueryKeys from "@/config/query-keys";
import { DATE_FORMAT_OPTIONS } from "@/Core/Helpers/DayjsHelpers";
import {
    UserFormDto,
    userFormDtoSchema,
    UserResponseDto,
} from "@/Dtos/user-dto";
import AvatarCard from "@/ui/avatar-card";
import AvatarLoaderCard from "@/ui/avatar-card-loader";
import DateCell from "@/ui/date-cell";
import { canAccess } from "@/utils/Utils";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { FcBiomass, FcUnlock } from "react-icons/fc";
import { Badge, Checkbox, Flex, Title } from "rizzui";
import { useModal } from "../shared/modal-views/use-modal";
import AssignRoleForm from "./assign-role-form";
import ResetPasswordForm from "./change-password-form";

const config: Config<UserResponseDto, UserFormDto> = {
    endPoint: EndPoints.user.crud,
    queryKey: useQueryKeys.users,
    columns: [],
    validationSchema: userFormDtoSchema,
    permissionsCreate: PermissionsLists.User.Create,
    formOptions: {
        customSize: "1200px",
        sendAsFormData: true,
        formClassName:
            "grid grid-cols-1 gap-5 @container md:grid-cols-2 lg:grid-cols-3 [&_label]:font-medium",
    },

    fields: [
        {
            name: "name",
            label: "Name",
            type: "text",
            hasFilter: true,
            // className: "w-auto",
            filter: {
                operation: Operation.like,
                // showOperations: true,
            },
        },
        {
            name: "email",
            label: "Email",
            type: "email",
            hasFilter: true,
            filter: {
                operation: Operation.eq,
            },
        },
        {
            name: "phoneNumber",
            label: "Phone Number",
            type: "phone",
            hasFilter: true,
            filter: {
                operation: Operation.like,
            },
        },
        {
            name: "sex",
            label: "Sex",
            type: "select",
            options: [
                {
                    label: "Male",
                    value: "male" as string,
                },
                {
                    label: "Female",
                    value: "female" as string,
                },
            ],
            hasFilter: true,
            filter: {
                operation: Operation.eq,
            },
            // props: {
            //     // getOptionLabel: (option) => option.label,
            //     // getOptionValue: (option) => option.value,
            //     optionClassName: "capitalize",
            // },
        },
        {
            name: "entityId",
            label: "Entity",
            type: "referTo",
            hasFilter: true,
            // className: "w-auto",
            referEndPoint: EndPoints.entity.referTo,
            filter: {
                operation: Operation.eq,
                // showOperations: true,
            },
        },
        {
            name: "rolesId",
            label: "Roles",
            type: "referToMultiple",
            hasFilter: true,
            referEndPoint: EndPoints.role.referTo,
            filter: {
                operation: Operation.eq,
            },
        },
        {
            name: "countryId",
            label: "Country",
            type: "referTo",
            hasFilter: true,
            // className: "w-auto",
            referEndPoint: EndPoints.country.referTo,
            filter: {
                operation: Operation.like,
                // showOperations: true,
            },
        },
        {
            name: "cityId",
            label: "City",
            type: "referTo",
            hasFilter: true,
            referEndPoint: EndPoints.city.referTo,
            filter: {
                operation: Operation.like,
                // showOperations: true,
            },
            dependsOn: {
                name: "countryId",
                filter: (e, watchedValue) =>
                    e?.["meta"]?.["countryId"].match(watchedValue),
            },
        },
        {
            name: "picture",
            label: "Picture",
            type: "file",
        },
        {
            name: "password",
            label: "Password",
            type: "password",
            createFormOnly: true,
        },
        {
            name: "confirmPassword",
            label: "Confirm Password",
            type: "password",
            createFormOnly: true,
        },

        {
            name: "createdAt",
            label: "Created At",
            type: "date",
            hasFilter: true,
            filterOnly: true,
            dateFormat: DATE_FORMAT_OPTIONS.L,
            // className: "w-auto",
            filter: {
                operation: Operation.gte,
                // showOperations: true,
            },
        },
    ],
    titles: {
        pageTitle: "Users",
    },
    filterProps: {
        useDrawer: true,
        // ignoreSorting: true,
        sortingClassName: "w-auto",
        // sortingMinWidth: "10px",
        directionClassName: "w-auto",
        // directionMinWidth: "10px",
    },
};

const columnHelper = createColumnHelper<UserResponseDto>();

const Columns = [
    columnHelper.display({
        id: "select",
        size: 50,
        header: ({ table }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select all Rows"
                checked={table.getIsAllPageRowsSelected()}
                onChange={() => table.toggleAllPageRowsSelected()}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select Row"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
            />
        ),
    }),

    columnHelper.accessor("name", {
        id: "name",
        size: 200,
        header: "Name",
        enableSorting: true,
        cell: ({ row }) => (
            <AvatarCard
                src={
                    (row.original.pictureIsLink
                        ? row.original.picture
                        : `${import.meta.env.VITE_APP_ImagePath}${row.original.picture}`) ??
                    ""
                }
                name={row.original?.name ?? ""}
                description={row.original.email}
            />
        ),
    }),
    columnHelper.accessor("phoneNumber", {
        id: "phoneNumber",
        size: 150,
        header: "Phone Number",
        cell: ({ row }) => row.original.phoneNumber,
    }),
    columnHelper.accessor("country.name", {
        id: "country.name",
        size: 150,
        header: "Country",
        cell: ({ row: { original } }) => (
            <AvatarLoaderCard
                name={original.country?.name ?? ""}
                description={original.city?.name ?? ""}
            />
        ),
    }),
    columnHelper.accessor("status", {
        id: "status",
        size: 140,
        header: "Status",
        cell: ({ row: { original } }) => (
            <DynamicBadge
                status={original.status}
                classNameBadge={getUserStatusColors(original.status)[1]}
                classNameText={getUserStatusColors(original.status)[0]}
            />
        ),
    }),
    columnHelper.accessor("sex", {
        id: "sex",
        size: 140,
        header: "Sex",
        cell: ({ row }) => (
            <span className="text-gray-700 capitalize">{row.original.sex}</span>
        ),
    }),
    columnHelper.accessor("entity.name", {
        id: "entity",
        size: 140,
        header: "Entity",
        cell: ({ row }) => (
            <Title
                as="h6"
                className="mb-0.5 !text-sm font-medium text-gray-700"
            >
                {row.original.entity?.name ?? "-"}
            </Title>
        ),
    }),
    columnHelper.display({
        id: "roles",
        size: 250,
        header: "Roles",
        cell: ({ row: { original } }) => (
            <Flex align="center" gap="2">
                {original?.roles?.map((per) => (
                    <Badge
                        rounded="lg"
                        key={per}
                        variant="outline"
                        className="border-muted font-normal text-gray-500"
                    >
                        {per}
                    </Badge>
                ))}
            </Flex>
        ),
    }),
    columnHelper.accessor("createdAt", {
        id: "createdAt",
        size: 160,
        header: "Created",
        cell: ({ row }) => (
            <DateCell
                date={new Date(Date.parse(row.original?.createdAt ?? ""))}
            />
        ),
    }),
    columnHelper.display({
        id: "actions",
        size: 80,
        cell: ({ row }) => {
            const { openModal } = useModal();
            const [isLoading, setLoading] = useState(false);
            const queryClient = useQueryClient();

            return (
                <TableActions
                    row={row.original}
                    onDelete={() => {
                        setLoading(true);
                        httpService(config.endPoint).delete(row.original.id!);
                        queryClient.refetchQueries({
                            queryKey: config.queryKey,
                        });
                        setLoading(false);
                    }}
                    actions={[
                        {
                            title: "Reset Password",
                            isLoading: isLoading,
                            icon: <FcUnlock className="size-4" />,
                            onClick: () =>
                                openModal({
                                    customSize: "550px",
                                    view: (
                                        <ResetPasswordForm
                                            editData={row.original}
                                            callback={() =>
                                                queryClient.refetchQueries({
                                                    queryKey: config.queryKey,
                                                })
                                            }
                                        />
                                    ),
                                }),
                            permission: PermissionsLists.User.ResetPassword,
                        },
                        {
                            title: "Assign Role",
                            isLoading: isLoading,
                            icon: <FcBiomass className="size-4" />,
                            onClick: () =>
                                openModal({
                                    customSize: "500px",

                                    view: (
                                        <AssignRoleForm
                                            editData={row.original}
                                            callback={() =>
                                                queryClient.refetchQueries({
                                                    queryKey: config.queryKey,
                                                })
                                            }
                                        />
                                    ),
                                }),
                            hide: () =>
                                !canAccess({
                                    permission:
                                        PermissionsLists.User.AssignRole,
                                }),
                        },
                        {
                            title: "Edit",
                            color: "gray",
                            isLoading: isLoading,
                            icon: <PencilIcon className="size-4" />,
                            onClick: () =>
                                openModal({
                                    view: (
                                        <CrudFormElements<
                                            UserResponseDto,
                                            UserFormDto
                                        >
                                            editData={{
                                                ...row.original,
                                                picture: "",
                                            }}
                                            config={{
                                                ...config,
                                                fields: config?.fields?.filter(
                                                    (a) => !a.createFormOnly,
                                                ),
                                            }}
                                        />
                                    ),
                                    customSize: config.formOptions?.customSize,
                                }),
                        },
                    ]}
                />
            );
        },
    }),
];

config.columns = Columns;

export default function MainPage() {
    return CrudPage<UserResponseDto, UserFormDto>({ config: config });
}
